import axios from 'axios';

const API_BASE_URL = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  async register(data) {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  async login(email, password) {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    
    const response = await axios.post(`${API_BASE_URL}/auth/login`, formData);
    const { access_token } = response.data;
    
    localStorage.setItem('token', access_token);
    
    // Get user info
    const userResponse = await api.get('/auth/me');
    localStorage.setItem('user', JSON.stringify(userResponse.data));
    
    return userResponse.data;
  },

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated() {
    return !!localStorage.getItem('token');
  },
};

export const beachService = {
  async list(city) {
    const params = city ? { city } : {};
    const response = await api.get('/beaches', { params });
    return response.data;
  },

  async getNearby(latitude, longitude, maxDistance = 10000) {
    const response = await api.get('/beaches/nearby', {
      params: { latitude, longitude, max_distance: maxDistance },
    });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/beaches/${id}`);
    return response.data;
  },
};

export const kioskService = {
  async list(beachId, isOpen) {
    const params = {};
    if (beachId) params.beach_id = beachId;
    if (isOpen !== undefined) params.is_open = isOpen;
    
    const response = await api.get('/kiosks', { params });
    return response.data;
  },

  async getNearby(latitude, longitude, maxDistance = 500) {
    const response = await api.get('/kiosks/nearby', {
      params: { latitude, longitude, max_distance: maxDistance },
    });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/kiosks/${id}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/kiosks', data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.patch(`/kiosks/${id}`, data);
    return response.data;
  },

  async getMyKiosks() {
    const response = await api.get('/kiosks/my/kiosks');
    return response.data;
  },
};

export const productService = {
  async list(kioskId, category, isAvailable) {
    const params = {};
    if (kioskId) params.kiosk_id = kioskId;
    if (category) params.category = category;
    if (isAvailable !== undefined) params.is_available = isAvailable;
    
    const response = await api.get('/products', { params });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/products', data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.patch(`/products/${id}`, data);
    return response.data;
  },
};

export const orderService = {
  async create(data) {
    const response = await api.post('/orders', data);
    return response.data;
  },

  async list(status) {
    const params = status ? { status } : {};
    const response = await api.get('/orders', { params });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  },

  async getByNumber(orderNumber) {
    const response = await api.get(`/orders/by-number/${orderNumber}`);
    return response.data;
  },

  async updateStatus(id, newStatus) {
    const response = await api.patch(`/orders/${id}/status`, null, {
      params: { new_status: newStatus },
    });
    return response.data;
  },
};

export const paymentService = {
  async createPayment(orderId, paymentMethod) {
    const response = await api.post('/payments/create', null, {
      params: { order_id: orderId, payment_method: paymentMethod },
    });
    return response.data;
  },

  async getByOrder(orderId) {
    const response = await api.get(`/payments/${orderId}`);
    return response.data;
  },

  async mockApprove(orderId) {
    const response = await api.post(`/payments/mock-approve/${orderId}`);
    return response.data;
  },
};

export default api;
